import { get, post, put, del } from '@/lib/api/client'
import type {
  ApiResponse,
  PaginatedResponse,
  PaginationParams,
  Application,
  ApplicationStats,
  CreateApplication,
  UpdateApplication,
  UpdateApplicationStatus,
  ApplicationFilterParams
} from '@/lib/api/types'

export const applicationService = {

  // get all applications with pagination
  getAllApplications: async (): Promise<PaginatedResponse<Application>> => {
    return await get<PaginatedResponse<Application>>('job-applications/private/all')
  },

  // Filter applications with pagination
  filterApplications: async (
    params: PaginationParams = { page: 1, size: 10 },
    filters: ApplicationFilterParams = {}
  ): Promise<ApiResponse<PaginatedResponse<Application>>> => {
    return await post<ApiResponse<PaginatedResponse<Application>>>(
      `job-applications/private/filter?size=${params.size}&page=${params.page}`,
      filters
    )
  },

  // Get application by ID (public endpoint)
  getApplicationById: async (applicationId: string): Promise<ApiResponse<Application>> => {
    return await get<ApiResponse<Application>>(`job-applications/private/${applicationId}`)
  },

  // Get application by ID (admin endpoint)
  getApplicationByIdAdmin: async (applicationId: string): Promise<ApiResponse<Application>> => {
    return await get<ApiResponse<Application>>(`job-applications/admin/private/${applicationId}`)
  },

  // Create a new application
  createApplication: async (data: CreateApplication): Promise<ApiResponse<Application>> => {
    return await post<ApiResponse<Application>>(
      'job-applications/admin/private',
      data
    )
  },

  // Update an existing application
  updateApplication: async (
    applicationId: string,
    data: UpdateApplication
  ): Promise<ApiResponse<Application>> => {
    return await put<ApiResponse<Application>>(
      `job-applications/admin/private/${applicationId}`,
      data
    )
  },

  // Update application status
  updateApplicationStatus: async (
    applicationId: string,
    data: UpdateApplicationStatus
  ): Promise<ApiResponse<null>> => {
    return await put<ApiResponse<null>>(
      `job-applications/admin/private/${applicationId}/status`,
      data
    )
  },

  // Delete a application
  deleteApplication: async (applicationId: string): Promise<ApiResponse<null>> => {
    return await del<ApiResponse<null>>(
      `job-applications/admin/private/${applicationId}`
    )
  },

  // Search applications
  searchApplications: async (
    searchBy?: string
  ): Promise<ApiResponse<Application[]>> => {
    const queryParams = new URLSearchParams()

    if (searchBy !== undefined) {
      queryParams.append('searchBy', searchBy)
    }

    return await get<ApiResponse<Application[]>>(
      `job-applications/private/search?${queryParams.toString()}`
    )
  },

  // Get application types
  getApplicationTypes: async (): Promise<ApiResponse<Array<{ value: string; displayName: string }>>> => {
    return await get<ApiResponse<Array<{ value: string; displayName: string }>>>(
      'job-applications/private/application-types'
    )
  },

  // get application statuses
  getApplicationStatuses: async (): Promise<ApiResponse<Array<{ value: string; displayName: string }>>> => {
    return await get<ApiResponse<Array<{ value: string; displayName: string }>>>(
      'job-applications/private/statuses'
    )
  },

  // Get application stats
  getApplicationStats: async (): Promise<ApiResponse<ApplicationStats>> => {
    return await get<ApiResponse<ApplicationStats>>('job-applications/private/stats')
  }
}

